<template>
  <div class="flex pb-6 justify-between gap-4">
    <div class="flex flex-col md:flex-row gap-4">
      <ExportFetch exportEndpoint="/supply/user/export" fileName="gas" exportType="gas"
        :date-range="isAdsUser()" :from-label="$t('admin.export.dateFrom')" :to-label="$t('admin.export.dateTo')"
        @export-started="handleExportStarted" @export-completed="handleExportCompleted"
        @export-failed="handleExportFailed" />

      <PrintTableComponent :title="$t('supplyGas.title')" :fields="translatedFields" :items="items"
        @print-started="() => console.log('Impresión iniciada')"
        @print-completed="() => console.log('Impresión completada')"
        @print-failed="(error) => console.error('Error al imprimir:', error)" />
    </div>
    <div class="flex flex-col-reverse items-end md:w-auto md:flex-row gap-2.5 md:items-center justify-center ">
      <RouterLink to="/add-gas">

        <ButtonWhite class="flex items-center gap-2 text-[#385660] w-full md:w-auto">
          {{ $t('supplyGas.buttons.addNew') }}
          <PlusIcon />
        </ButtonWhite>
      </RouterLink>

    </div>

  </div>

  <TitlePrimary class="w-full" :title="$t('supplyGas.title')" />

  <div v-if="route.query && route.query.id" class="mt-2 flex items-center gap-2">
    <ButtonWhite class="flex items-center gap-2" @click="removeIdParam">{{ t('common.backToSearch')}}</ButtonWhite>
    <div class="text-sm text-[#385660]">{{ noItemsForAccount }}</div>
  </div>

  <div class="md:hidden py-4 px-2 overflow-x-auto">
    <CardTable v-if="role == 'superadmin'" :fields="translatedFields"
      :items="items.length > 0 ? [items[currentItemIndex]] : []" :showOptions="true" route="/gas-detail"
      :listOptions="translatedOptionsList" />
    <CardTable v-else :fields="translatedFields" :items="items.length > 0 ? [items[currentItemIndex]] : []"
      :showOptions="false" route="/gas-detail" :listOptions="translatedOptionsList" />
    <MobilePaginateComponent v-if="items.length > 0" :totalPages="totalPages" :modelValue="currentPage"
      :totalItems="totalItems" :currentItemIndex="currentItemIndex" :itemsPerPage="10"
      @update:modelValue="handlePageChange" @update:currentItemIndex="handleItemChange" />
  </div>

  <div class="hidden md:block">

    <TableComponent :filter-definitions="filterDefinitions" :filter-error="filterError" v-model:filters="filters" @filter-change="onFilterChange" :fields="translatedFields" :items="items" :showOptions="true" route="gas-detail"
      :listOptions="translatedOptionsList" />

    <PaginateComponent :totalPages="totalPages" :paginationInfo="paginationInfo" :modelValue="currentPage" @update:modelValue="handlePageChange" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useExportStore } from '@/stores/exportStore';
import { useRoute, useRouter } from 'vue-router';
import { useTableFilters } from '@/composables/useTableFilters';

import TableComponent from '@/components/table/TableComponent.vue';
import PaginateComponent from '@/components/paginate/PaginateComponent.vue';
import { usePaginationInfo } from '@/composables/usePaginationInfo';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import ExportFetch from '@/components/fetch/exportFetch.vue';
import { isAdsUser } from '@/services/userRoles';
import PrintTableComponent from '@/components/print/PrintTableComponent.vue';

import PlusIcon from '@/assets/svg/PlusIcon.vue';
import TitlePrimary from '@/components/title/title-primary.vue';
import CardTable from '@/components/table/CardTable.vue';
import MobilePaginateComponent from '@/components/paginate/MobilePaginateComponent.vue';
import { normalizeSupplyRows } from '@/services/supplyRows';
import { supplyRequestOptions } from '@/services/supplyRequestOptions';

const translatedFields = computed(() => [
  { key: "cups", label: t('supplyGas.table.cups') },
  // Sin CUPS la fila no tiene nada que la identifique, y el legacy deja muchos
  // contratos sin él; el nombre de la finca sí está siempre.
  { key: "property_name", label: t('supplyGas.table.property') },
  { key: "address", label: t('supplyGas.table.address') },
  { key: "postal_code", label: t('supplyGas.table.postalCode') },
  { key: "municipality", label: t('supplyGas.table.municipality') },
  { key: "province", label: t('supplyGas.table.province') },
  { key: "rate", label: t('supplyGas.table.rate') },
  { key: "company", label: t('supplyGas.table.company') },
  { key: "opciones", label: t('supplyGas.table.options') }
]);

// "Crear solicitud" desde el CUPS: el formulario recibe el suministro de origen.
const translatedOptionsList = computed(() => supplyRequestOptions(t));


const role = localStorage.getItem('role');
const route = useRoute();
const router = useRouter();

// --- Estado ---
const { t } = useI18n();

// --- filtros de cabecera ---
const filterDefinitions = computed(() => [
  { key: 'cups', type: 'text' },
  { key: 'property_name', type: 'text' },
  { key: 'address', type: 'text' },
  { key: 'postal_code', type: 'text' },
  { key: 'municipality', type: 'text' },
  { key: 'province', type: 'text' },
  { key: 'rate', type: 'text' },
  { key: 'company', type: 'text' },
]);

const { filters, isFiltering, filterError, runSearch, onFilterChange } = useTableFilters({
  endpoint: () => (route.query?.id || role !== 'superadmin' ? '/supply/user/search' : '/supply/superadmin/search'),
  perPage: 10,
  translate: t,
  extraParams: () => ({ type: 'gas', ...(route.query?.id ? { id: route.query.id } : {}) }),
  onResults: (data) => {
    const page = data?.value;
    if (!page) return;
    items.value = normalizeSupplyRows(page.data || []);
    totalPages.value = page.last_page || 1;
    setPaginationInfo(page);
    totalItems.value = page.total ?? 0;
    currentPage.value = page.current_page || 1;
    isSearchActive.value = true;
  },
  onCleared: () => {
    isSearchActive.value = false;
    currentPage.value = 1;
    fetchDataFromApi();
  },
});
const items = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const { paginationInfo, setPaginationInfo } = usePaginationInfo();
const searchText = ref('');
const selectedFilterName = ref(null);
const isSearchActive = ref(false);
const exportStore = useExportStore();
const currentItemIndex = ref(0);
const totalItems = ref(0);
// Mensaje cuando no hay resultados para el id pasado
const noItemsForAccount = ref('');

// --- Funciones de exportación ---
const handleExportStarted = () => {
  console.log('Exportación iniciada');
  exportStore.startExport('Exportando suministros de gas...');
};

const handleExportCompleted = () => {
  console.log('Exportación completada');
  exportStore.completeExport();
};

const handleExportFailed = () => {
  console.log('Exportación fallida');
  exportStore.failExport('Error al exportar suministros de gas');
};

// --- funciones ---
const fetchDataFromApi = async () => {
  isSearchActive.value = false;
  try {
    // Si viene un id por query, forzar endpoint de usuario
    const endpoint = route.query && route.query.id
      ? '/supply/user/list/gas'
      : (role == 'superadmin' ? '/supply/superadmin/list/gas' : '/supply/user/list/gas');

    const params = {
      per_page: 10,
      page: currentPage.value
    };
    if (route.query && route.query.id) params.id = route.query.id;

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${endpoint}`,
      {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params
      }
    );
    console.log(response.data.value)
    if (response.data?.value.data) {
      console.log('axios', response.data.value.data)
      items.value = normalizeSupplyRows(response.data.value.data)
      totalPages.value = response.data.value.last_page;
      setPaginationInfo(response.data.value);
      totalItems.value = response.data.value.total ?? 0;
      console.log(items)
      noItemsForAccount.value = '';
    }
  } catch (error) {
    items.value = [];
    if (error.response && error.response.status === 404) {
      noItemsForAccount.value = t('supplyGas.noItemsForAccount') || 'No hay suministros para esta cuenta';
    } else {
      console.error('Error fetching data:', error);
    }
  }
};

function removeIdParam() {
  const newQuery = { ...route.query };
  if (newQuery && newQuery.id) {
    delete newQuery.id;
    router.replace({ path: route.path, query: newQuery }).catch(() => {});
  }
  noItemsForAccount.value = '';
  fetchDataFromApi();
}

// Manejo de cambio de página
const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  if (isFiltering.value) {
    await runSearch(newPage);
  } else {
    await fetchDataFromApi();
  }

};

const handleItemChange = (newIndex) => {
  if (newIndex >= 0 && newIndex < items.value.length) {
    currentItemIndex.value = newIndex;
  }
};

const props = defineProps({
  searchEndpoint: {
    type: String,
    default: '/supply/user/search'
  }
});

async function performSearch(page = 1) {
  try {
    const params = {
      query: searchText.value,
      per_page: 10,
      page: page, // Usar página recibida como parámetro
      search_by: selectedFilterName.value?.key
    };

    // Si existe id en la query, forzar el endpoint de usuario y añadir id
    let endpointToUse = props.searchEndpoint;
    if (route && route.query && route.query.id) {
      endpointToUse = '/supply/user/search';
      params.id = route.query.id;
    }

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${endpointToUse}`,
      {
        params
      }
    );
    console.log('search-results', response.data);
  } catch (error) {
    console.log('search-error', error);
  }
}
defineExpose({ performSearch });

onMounted(() => {
  fetchDataFromApi();
});

</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
